import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Coffee, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NAV_LINKS = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Features', href: '/pricing#features-section' },
  { label: 'Help & Support', href: 'mailto:support@cafelawn.com', external: true },
];

const PublicNavbar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isLinkActive = (href: string, external?: boolean) => {
    if (external) return false;
    const [path, hash] = href.split('#');
    if (location.pathname !== path) return false;
    if (hash) return location.hash === `#${hash}`;
    return !location.hash;
  };

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200/80 dark:border-gray-700/80 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/pricing" className="flex items-center gap-2 text-gray-900 dark:text-white font-bold">
          <Coffee className="w-6 h-6 text-purple-600" />
          <span>CafeLawn</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.external ? (
              <a
                key={link.label}
                href={link.href}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  isLinkActive(link.href, link.external)
                    ? 'text-gray-900 dark:text-white underline underline-offset-4'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          {currentUser ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold flex items-center justify-center"
              >
                {(currentUser.email?.[0] || 'U').toUpperCase()}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
                  <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 truncate border-b border-gray-100 dark:border-gray-700">
                    {currentUser.email}
                  </p>
                  <button
                    type="button"
                    onClick={() => void logout()}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/signin"
              className="px-4 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:opacity-90 transition-opacity"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default PublicNavbar;
